/**
 * Challenge hooks — TanStack Query for challenges CRUD, membership, habits.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  createChallenge,
  updateChallenge,
  fetchUserChallenges,
  fetchChallengeProfile,
  fetchLeaderboardPage,
  fetchChallengeReviews,
  submitChallengeReview,
  joinChallenge,
  autoJoinCreator,
  leaveChallenge,
  createChallengeHabit,
  deleteChallengeHabit,
  copyChallengeHabitsToUser,
  deactivateChallengeHabits,
  fetchChallengeStats,
  fetchCompletedChallenges,
  fetchBrowseChallenges,
  fetchChallengeCategories,
  fetchAdBanners,
  fetchCompetitions,
  searchChallenges,
} from '@turnifly/shared/api'
import type {
  CreateChallengeParams,
  CreateChallengeHabitParams,
  ChallengeStats,
  CompletedChallengeItem,
  BrowseChallengeItem,
  CategoryItem,
  AdBannerItem,
} from '@turnifly/shared/api'
import type {
  ChallengeProfileResponse,
  LeaderboardEntry,
  ReviewItem,
} from '@turnifly/shared/types'

export const challengeKeys = {
  all: ['challenges'] as const,
  user: (userId: string) => ['challenges', 'user', userId] as const,
  profile: (id: string) => ['challenges', 'profile', id] as const,
  leaderboard: (id: string, page: number) =>
    ['challenges', 'leaderboard', id, page] as const,
  reviews: (id: string) => ['challenges', 'reviews', id] as const,
  stats: (userId: string) => ['challenges', 'stats', userId] as const,
  completed: (userId: string) => ['challenges', 'completed', userId] as const,
  browse: (category: string | null, types: string[], sort: string, page: number) =>
    ['challenges', 'browse', category, types, sort, page] as const,
  categories: ['challenges', 'categories'] as const,
  banners: ['challenges', 'banners'] as const,
  competitions: (status: string, page: number) =>
    ['challenges', 'competitions', status, page] as const,
  search: (query: string, category: string | null) =>
    ['challenges', 'search', query, category] as const,
} as const

// ── User's Challenges (profile tab) ────────────────────────

export function useUserChallenges(userId: string | undefined) {
  return useQuery({
    queryKey: challengeKeys.user(userId ?? ''),
    queryFn: () => fetchUserChallenges(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  })
}

// ── Challenge Profile (single RPC — all data) ────────────────

export function useChallengeProfile(challengeId: string | undefined) {
  return useQuery<ChallengeProfileResponse>({
    queryKey: challengeKeys.profile(challengeId ?? ''),
    queryFn: () => fetchChallengeProfile(challengeId!),
    enabled: !!challengeId,
    staleTime: 30_000,
  })
}

// ── Create Challenge ────────────────────────────────────────

export function useCreateChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      params: CreateChallengeParams & { autoJoin?: boolean },
    ) => {
      const challenge = await createChallenge(params)
      const created = challenge as { id: string }

      if (params.autoJoin !== false) {
        await autoJoinCreator(created.id, params.creatorId)
      }

      return challenge
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: challengeKeys.all })
    },
  })
}

// ── Update Challenge ────────────────────────────────────────

export function useUpdateChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      challengeId,
      updates,
    }: {
      challengeId: string
      updates: Record<string, unknown>
    }) => updateChallenge(challengeId, updates),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(variables.challengeId),
      })
      queryClient.invalidateQueries({ queryKey: challengeKeys.all })
    },
  })
}

// ── Join Challenge ──────────────────────────────────────────

export function useJoinChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      challengeId,
      userId,
      startDate,
      endDate,
    }: {
      challengeId: string
      userId: string
      startDate: string | null
      endDate: string | null
    }) => {
      const result = await joinChallenge(challengeId, userId)
      await copyChallengeHabitsToUser(challengeId, userId, startDate, endDate)
      return result
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(variables.challengeId),
      })
      queryClient.invalidateQueries({ queryKey: challengeKeys.all })
      queryClient.invalidateQueries({ queryKey: ['habits'] })
    },
  })
}

// ── Leave Challenge ─────────────────────────────────────────

export function useLeaveChallenge() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      challengeId,
      userId,
    }: {
      challengeId: string
      userId: string
    }) => {
      await leaveChallenge(challengeId, userId)
      await deactivateChallengeHabits(challengeId, userId)
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(variables.challengeId),
      })
      queryClient.invalidateQueries({ queryKey: challengeKeys.all })
      queryClient.invalidateQueries({ queryKey: ['habits'] })
    },
  })
}

// ── Create Challenge Habit ──────────────────────────────────

export function useCreateChallengeHabit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: CreateChallengeHabitParams) =>
      createChallengeHabit(params),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(variables.challengeId),
      })
    },
  })
}

// ── Delete Challenge Habit ──────────────────────────────────

export function useDeleteChallengeHabit(challengeId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (habitId: string) => deleteChallengeHabit(habitId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(challengeId),
      })
    },
  })
}

// ── Leaderboard Page (paginated) ─────────────────────────────

export function useLeaderboardPage(
  challengeId: string | undefined,
  page: number,
) {
  return useQuery<LeaderboardEntry[]>({
    queryKey: challengeKeys.leaderboard(challengeId ?? '', page),
    queryFn: () => fetchLeaderboardPage(challengeId!, page * 20, 20),
    enabled: !!challengeId,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })
}

// ── Challenge Reviews (paginated) ────────────────────────────

export function useChallengeReviewsList(
  challengeId: string | undefined,
  page: number,
) {
  return useQuery<ReviewItem[]>({
    queryKey: [...challengeKeys.reviews(challengeId ?? ''), page],
    queryFn: () => fetchChallengeReviews(challengeId!, 20, page * 20),
    enabled: !!challengeId,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })
}

// ── Submit Review ────────────────────────────────────────────

export function useSubmitReview() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      challengeId,
      rating,
      text,
    }: {
      challengeId: string
      rating: number
      text: string | null
    }) => submitChallengeReview(challengeId, rating, text),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: challengeKeys.profile(variables.challengeId),
      })
      queryClient.invalidateQueries({
        queryKey: challengeKeys.reviews(variables.challengeId),
      })
    },
  })
}

// ── Challenge Stats (for profile stats tab) ────────────────

export function useChallengeStats(userId: string | undefined) {
  return useQuery<ChallengeStats>({
    queryKey: challengeKeys.stats(userId ?? ''),
    queryFn: () => fetchChallengeStats(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

// ── Completed Challenges (for profile achievements section) ──

export function useCompletedChallenges(userId: string | undefined) {
  return useQuery<CompletedChallengeItem[]>({
    queryKey: challengeKeys.completed(userId ?? ''),
    queryFn: () => fetchCompletedChallenges(userId!),
    enabled: !!userId,
    staleTime: 60_000,
  })
}

// ── Browse Challenges (All/Turnifly/Experts tabs) ──────────

export function useBrowseChallenges(params: {
  category?: string | null
  types?: string[]
  statuses?: string[]
  sort?: 'popular' | 'newest' | 'rating'
  page?: number
  enabled?: boolean
}) {
  const page = params.page ?? 0
  const limit = 20
  return useQuery<BrowseChallengeItem[]>({
    queryKey: challengeKeys.browse(
      params.category ?? null,
      params.types ?? [],
      params.sort ?? 'popular',
      page,
    ),
    queryFn: () => fetchBrowseChallenges({
      category: params.category,
      types: params.types,
      statuses: params.statuses,
      sort: params.sort,
      limit,
      offset: page * limit,
    }),
    enabled: params.enabled !== false,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })
}

// ── Categories ─────────────────────────────────────────────

export function useChallengeCategories() {
  return useQuery<CategoryItem[]>({
    queryKey: challengeKeys.categories,
    queryFn: fetchChallengeCategories,
    staleTime: 300_000,
  })
}

// ── Ad Banners ─────────────────────────────────────────────

export function useAdBanners() {
  return useQuery<AdBannerItem[]>({
    queryKey: challengeKeys.banners,
    queryFn: fetchAdBanners,
    staleTime: 300_000,
  })
}

// ── Competitions (by status) ────────────────────────────────

export function useCompetitionsList(
  status: 'active' | 'upcoming' | 'completed',
  page: number,
) {
  const limit = 20
  return useQuery<BrowseChallengeItem[]>({
    queryKey: challengeKeys.competitions(status, page),
    queryFn: () => fetchCompetitions({ status, limit, offset: page * limit }),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  })
}

// ── Search Challenges ──────────────────────────────────────

export function useSearchChallenges(
  query: string,
  category?: string | null,
) {
  return useQuery<BrowseChallengeItem[]>({
    queryKey: challengeKeys.search(query, category ?? null),
    queryFn: () => searchChallenges(query, category),
    enabled: query.length >= 2,
    staleTime: 30_000,
  })
}
